import Box from '@mui/material/Box';
import { type Theme } from '@mui/material/styles';

import { controlTokens, surfaceTokens, typography } from '@/theme/themePrimitives';

const SYMBOL_URL = '/icon/logo-black-transparent-480.png';

type BrandLockupSize = 'bar' | 'stage';

interface Props {
  /** Product descriptor after the wordmark ("Partners"), shown in capitals. */
  descriptor: string;
  size: BrandLockupSize;
}

/**
 * Horizontal lockup with descriptor (brand/visual-identity.md › Lockups): symbol, BODYPRINT in
 * Geist Bold and the descriptor in Geist Regular, same size, tracking 0.14em, descriptor 0.45em
 * after the wordmark. Symbol, gap and text size are the ones of the BODYPRINT lockup on the web
 * sign-in (bodyprint-web `AuthPageLayout`).
 */
const BrandLockup = ({ descriptor, size }: Props) => (
  <Box sx={sx.root(size)}>
    <Box sx={sx.symbol(size)} />
    <Box component="span" sx={sx.word}>
      BODYPRINT
      <Box component="span" sx={sx.descriptor}>
        {descriptor}
      </Box>
    </Box>
  </Box>
);

export default BrandLockup;

interface Metric {
  xs: number;
  lg: number;
}

// Pixel values per size, as on the web sign-in: the bar keeps the phone size at every width.
const METRICS: Record<BrandLockupSize, Record<'symbol' | 'gap' | 'text', Metric>> = {
  bar: { symbol: { xs: 22, lg: 22 }, gap: { xs: 8, lg: 8 }, text: { xs: 14, lg: 14 } },
  stage: { symbol: { xs: 22, lg: 26 }, gap: { xs: 8, lg: 10 }, text: { xs: 14, lg: 17 } },
};

// Strings, not numbers: MUI multiplies a numeric `gap` by the spacing unit.
const px = ({ xs, lg }: Metric) => ({ xs: `${xs}px`, lg: `${lg}px` });

const sx = {
  root: (size: BrandLockupSize) => (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: px(METRICS[size].gap),
    fontSize: px(METRICS[size].text),
    minHeight: 44,
    color: controlTokens.primary,
    ...theme.applyStyles('dark', { color: 'text.primary' }),
  }),
  symbol: (size: BrandLockupSize) => (theme: Theme) => ({
    height: px(METRICS[size].symbol),
    aspectRatio: '673 / 787',
    flex: 'none',
    bgcolor: controlTokens.primary,
    mask: `url(${SYMBOL_URL}) center / contain no-repeat`,
    WebkitMask: `url(${SYMBOL_URL}) center / contain no-repeat`,
    ...theme.applyStyles('dark', { bgcolor: surfaceTokens.paper }),
  }),
  word: {
    fontFamily: typography.logo.fontFamily,
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: '0.14em',
    whiteSpace: 'nowrap',
    color: 'inherit',
  },
  descriptor: {
    ml: '0.45em',
    fontFamily: typography.fontFamily,
    fontWeight: 400,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
};
